import { useNavigate, Link } from 'react-router-dom';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FaCog, FaEye, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { pdf } from '@react-pdf/renderer';
import { useFirebaseAuth } from '../../contexts/FirebaseAuthProvider';
import EditPanel from './EditPanel';
import Resume from './Resume';
import ResumePDF from './ResumePDF';
import WholeResumeService from './WholeResumeService';
import ChatbotPopup from './ChatbotPopup';
import CreditGateModal from './CreditGateModal';
import ResumeUpdateModal from './ResumeUpdateModal';
import characterLimits from './characterLimits';
import Popup from './Popup';
import FocusTrap from 'focus-trap-react';
import ResumeStorageService from './ResumeStorageService';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { useSEO } from '../../hooks/useSEO';
// Manual cookie modal removed
import axios from 'axios';

// CSS Imports
import '../styles/base.css';
import '../styles/layout.css';
import '../styles/components.css';
import '../styles/editPanel.css';
import '../styles/chatbot.css';
import '../styles/logoBanner.css';
import '../styles/berry.css';
import '../styles/settings.css';
import '../styles/resumePanel.css';
import '../styles/popup.css';
import '../styles/login.css';
import '../styles/helpButton.css';

const DesktopApp = () => {
  const { user, signOut, getToken } = useFirebaseAuth();
  const navigate = useNavigate();
  const { tier, status, credits, fetchSubscription } = useSubscription();
  const isGated = tier === 'free' || !tier || status === 'trialing';
  const { darkMode, toggleDarkMode } = useDarkMode();

  // Homepage SEO
  useSEO({
    title: 'Career Vision AI | Professional AI Resume Builder & Interview Simulator',
    description: 'Land your dream job with Career Vision AI. Create ATS-optimized resumes, write AI cover letters, and practice with our voice-enabled AI Interview Simulator.',
    canonical: 'https://careervisionai.com',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://careervisionai.com/#webpage",
          "url": "https://careervisionai.com",
          "name": "Career Vision AI | Professional AI Resume Builder & Interview Simulator",
          "description": "AI-powered resume builder, cover letter generator, and interview simulator to help you land your dream job.",
          "isPartOf": { "@id": "https://careervisionai.com/#website" },
          "about": { "@id": "https://careervisionai.com/#organization" }
        },
        {
          "@type": "SoftwareApplication",
          "name": "Career Vision AI",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "description": "AI-powered resume builder, cover letter generator, and interview simulator.",
          "url": "https://careervisionai.com",
          "offers": {
            "@type": "AggregateOffer",
            "priceCurrency": "USD",
            "lowPrice": "0",
            "highPrice": "24.99",
            "offerCount": "4"
          },
          "provider": { "@id": "https://careervisionai.com/#organization" }
        }
      ]
    }
  });

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const [creditModalType, setCreditModalType] = useState('pdf_confirm');
  const [onCreditModalConfirm, setOnCreditModalConfirm] = useState(null);
  const [proposedUpdate, setProposedUpdate] = useState(null); // { sectionKey, sectionName, currentContent, proposedContent }

  const [formData, setFormData] = useState({
    name: 'Guest',
    title: 'Senior Software Engineer',
    location: 'San Francisco, CA',
    email: 'john.doe@example.com',
    phone: '+1 (555) 019-2834',
    summary:
      'Innovative and detail-oriented Software Engineer with 6+ years of experience designing, building, and deploying scalable web applications. Proficient in modern JavaScript frameworks, cloud infrastructure, and agile methodologies.',
    experience:
      'Senior Software Engineer | TechSolutions Inc. | 2022 - Present\n- Led a team of 4 developers to redesign and migrate a legacy monolithic platform to a modern microservices architecture, improving system uptime to 99.9%.\n- Integrated scalable REST and GraphQL APIs using Node.js and TypeScript, reducing client page load latency by 35%.\n- Implemented automated CI/CD deployment pipelines on AWS, decreasing software release cycle time by 40%.\n\nSoftware Developer | InnovateWeb Co. | 2019 - 2022\n- Developed and maintained responsive web applications using React and Redux, enhancing user engagement metrics by 20%.\n- Optimized database query performance in PostgreSQL, resulting in a 50% speedup for core dashboard search features.',
    skills:
      'JavaScript / TypeScript\nReact / Next.js\nNode.js / Express\nSQL / PostgreSQL\nAmazon Web Services (AWS)\nGit / CI/CD Pipelines',
    education: 'Bachelor of Science in Computer Science\nState University, 2015 - 2019',
    publications:
      '- Author, "Building Scalable Microservices with Node.js" — TechBlog Publications, March 2024\n- Core Contributor, Open-source React UI Framework "SleekUI" (GitHub, 2K+ Stars)',
    jobDescription: '',
    jobPostUrl: '',
    companyName: '',
    targetRole: '',
    coverLetter: '',
    applicantAddress: '',
    recipientName: '',
    recipientJobTitle: '',
    coverLetterBody: '',
    whyCompany: '',
    keyAchievements: '',
    keyRoleSkills: '',
    jobPreview: null,
  });

  // Persist formData to localStorage so InterviewSimulator can access resume/job context
  useEffect(() => {
    localStorage.setItem('careerVisionFormData', JSON.stringify(formData));
  }, [formData]);

  const handleCopyAttempt = (e) => {
    if (isGated) {
      e.preventDefault();
      alert("Copying text is locked during the free trial. Upgrade to print or download your resume.");
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isGated && (e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        alert("Printing is disabled during the free trial. Upgrade to print or download your resume.");
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isGated]);

  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const statusTimeoutRef = useRef(null);
  const [activeSection, setActiveSection] = useState(null);
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [resumeScale, setResumeScale] = useState(0.8);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false); // New state for spinning
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuToggleRef = useRef(null);
  const slideNavRef = useRef(null);
  // Cookie modal removed for automated flow

  const resumeRef = useRef(null);
  const zoomContainerRef = useRef(null);
  const settingsPopupRef = useRef(null);
  const settingsButtonRef = useRef(null);
  const previewWindowRef = useRef(null); // reference to the popup window

  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const isPopupVisibleRef = useRef(isPopupVisible);
  useEffect(() => { isPopupVisibleRef.current = isPopupVisible; }, [isPopupVisible]);

  // Ensure we only run the initial resume load once per mount
  const hasInitialLoadRef = useRef(false);

  const chatbotRef = useRef(null);

  // getToken is retrieved directly from useFirebaseAuth above

  // Dynamic auth gating handles login checks on-demand for AI features

  // Handle adding/removing 'dark-mode' class and updating localStorage
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark-mode');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const updateLogoBanner = () => {
      const logoBanner = document.querySelector('.logo-banner img');
      if (logoBanner) {
        if (darkMode) {
          logoBanner.classList.remove('light-mode-logo');
          logoBanner.classList.add('dark-mode-logo');
        } else {
          logoBanner.classList.remove('dark-mode-logo');
          logoBanner.classList.add('light-mode-logo');
        }
      }
    };

    const timeoutId = setTimeout(updateLogoBanner, 0);

    return () => clearTimeout(timeoutId);
  }, [darkMode]);

  const handleDarkModeToggle = () => {
    toggleDarkMode(); // From the useDarkMode hook
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 700);
  };

  const toggleChatbot = useCallback(() => {
    setIsChatbotOpen(prev => !prev);
  }, []);

  const handleShowCreditGate = useCallback((type) => {
    setCreditModalType(type);
    setIsCreditModalOpen(true);
  }, []);

  // Function to close settings with animation
  const closeSettings = useCallback(() => {
    if (isClosing) return; // Prevent multiple close triggers
    setIsClosing(true);
    setTimeout(() => {
      setIsSettingsOpen(false);
      setIsClosing(false);
      // Return focus to settings button for accessibility
      if (settingsButtonRef.current) {
        settingsButtonRef.current.focus();
      }
    }, 500); // Duration should match the slideOut animation duration in CSS (0.5s)
  }, [isClosing]);

  // Function to handle settings icon click with spinning
  const handleSettingsClick = () => {
    if (isSpinning) return; // Prevent multiple spins
    setIsSpinning(true);
    toggleSettingsMenu();
  };

  // Effect to remove spinning class after animation
  useEffect(() => {
    if (isSpinning) {
      const timer = setTimeout(() => {
        setIsSpinning(false);
      }, 1000); // Duration matches the spin animation (1s)
      return () => clearTimeout(timer);
    }
  }, [isSpinning]);

  // Toggle settings menu
  const toggleSettingsMenu = () => {
    if (isClosing) return; // Prevent toggling during animation
    if (isSettingsOpen) {
      closeSettings();
    } else {
      setIsSettingsOpen(true);
    }
  };

  // Click-Outside and Escape Key Handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        settingsPopupRef.current &&
        !settingsPopupRef.current.contains(event.target) &&
        settingsButtonRef.current &&
        !settingsButtonRef.current.contains(event.target)
      ) {
        if (isSettingsOpen) {
          closeSettings();
        }
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && isSettingsOpen) {
        closeSettings();
      }
    };

    if (isSettingsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isSettingsOpen, closeSettings]);

  const changeTemplate = (template) => {
    console.log(`Changing to template: ${template}`);
  };

  const handleCoverLetterGenerate = async () => {
    if (!user) {
      navigate('/sign-in');
      return;
    }
    if (isGated && (credits?.remainingCredits || 0) <= 0) {
      setCreditModalType('ai_insufficient');
      setIsCreditModalOpen(true);
      return;
    }
    try {
      clearStatusMessage();
      setIsLoading(true);
      setStatusMessage({ text: 'Generating cover letter...', duration: 5000 });

      // Clear any stale job preview immediately so the UI doesn't show an old job during a new run
      // Also clear jobDescription if user didn't paste one and a URL is provided, to avoid showing a previous run's advert
      setFormData((prev) => {
        const pasted = (prev.jobDescription || '').trim();
        const hasUrl = (prev.jobPostUrl || '').trim().length > 7;
        const shouldClearJD = !pasted && hasUrl;
        return { ...prev, jobPreview: null, ...(shouldClearJD ? { jobDescription: '' } : {}) };
      });

      // If a URL is provided, open a small preview window to that exact page for user feedback (synchronously to avoid blockers)
      try {
        const rawUrl = (formData.jobPostUrl || '').trim();
        const hasUrl = rawUrl.length > 7;
        if (hasUrl) {
          const absoluteUrl = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
          const features = 'width=520,height=740,menubar=no,toolbar=no,location=yes,status=no,resizable=yes,scrollbars=yes';
          const w = window.open(absoluteUrl, 'jobPreviewWindow', features);
          if (w) previewWindowRef.current = w;
          setStatusMessage({ text: 'Opened job page in a small window. Crawling and summarizing…', duration: 6000 });
        }
      } catch {}

      const updateStatus = (message) => {
        setStatusMessage({ text: message, duration: 5000 });
      };

      let authToken = null;
      try {
        authToken = await (getToken ? getToken() : null);
        if (authToken) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
        }
      } catch {}

  let result;
      try {
        updateStatus('Starting automated fetch and summarization…');
        result = await WholeResumeService.generateCoverLetter(
          formData,
          updateStatus
        );
      } catch (err) {
        // If automated fetch failed due to auth, proceed with resume-only letter and a helpful placeholder
        const urlVal = (formData.jobPostUrl || '').trim();
        const isAuthBlock = /403|unauthorized|sign[- ]?in/i.test(err?.message || '') || /403/.test(String(err));
        if (urlVal && isAuthBlock) {
          setStatusMessage({ text: 'Unable to auto-fetch the job post. Generating a resume-based cover letter and inserting a placeholder in the job advert.', duration: 8000 });
          const placeholder = [
            `We couldn't auto-fetch the job posting from: ${urlVal}`,
            '',
            'Please paste the full job advert here to personalize your cover letter and resume alignment.',
            'In the meantime, a general cover letter has been generated based on your resume.'
          ].join('\n');
          const resultResumeOnly = await WholeResumeService.generateCoverLetter({ ...formData, jobDescription: '' }, updateStatus)
            .catch(() => ({ coverLetter: formData.coverLetter || '', jobDescription: '' }));
          const nextCover = (resultResumeOnly && resultResumeOnly.coverLetter) || formData.coverLetter || '';
          updateStatus('Auto-filling job advert…');
          setFormData((prev) => ({
            ...prev,
            coverLetter: nextCover,
            coverLetterBody: nextCover,
            jobDescription: placeholder
          }));
          setStatusMessage({ text: 'Cover letter generation complete.', duration: 5000 });
          // Close the preview window if opened
          try {
            if (previewWindowRef.current && !previewWindowRef.current.closed) {
              setStatusMessage({ text: 'Closing preview window…', duration: 2000 });
              setTimeout(() => {
                try { previewWindowRef.current.close(); } catch {}
                previewWindowRef.current = null;
              }, 700);
            }
          } catch {}
          setIsLoading(false);
          return;
        }
        throw err;
      }

      // Conflict handling: if pasted advert and URL conflict, warn user and stop
      if (result && result.conflict) {
        const reason = result.conflictInfo?.reason || 'Different job postings detected.';
        setStatusMessage({
          text: `Conflict detected between pasted advert and URL (${reason}). Please delete one of the inputs and try again.`,
          duration: 9000,
        });
        // Still update the job preview so the user sees the current job context
        if (result.jobPreview) {
          setFormData((prev) => ({
            ...prev,
            jobPreview: result.jobPreview,
          }));
        }
        // Close the preview window if opened
        try {
          if (previewWindowRef.current && !previewWindowRef.current.closed) {
            setTimeout(() => {
              try { previewWindowRef.current.close(); } catch {}
              previewWindowRef.current = null;
            }, 700);
          }
        } catch {}
        setIsLoading(false);
        return; // do not overwrite fields; user must resolve
      }

      // Compose a safe fallback jobDescription from jobPreview if result.jobDescription is missing
      const composeAdvert = (prevJD, jp) => {
        try {
          if (prevJD && prevJD.trim()) return prevJD;
          if (!jp) return prevJD;
          const parts = [];
          if (jp.summary) parts.push(`Summary:\n${String(jp.summary).trim()}`);
          if (Array.isArray(jp.responsibilities) && jp.responsibilities.length) {
            parts.push(`Responsibilities:\n- ${jp.responsibilities.map(b => String(b).trim()).filter(Boolean).join('\n- ')}`);
          }
          if (Array.isArray(jp.requirements) && jp.requirements.length) {
            parts.push(`Requirements:\n- ${jp.requirements.map(b => String(b).trim()).filter(Boolean).join('\n- ')}`);
          }
          return parts.join('\n\n').trim();
        } catch {
          return prevJD;
        }
      };

  let nextJobDescription = typeof result === 'object' && result.jobDescription ? result.jobDescription : formData.jobDescription;
  let nextCoverLetter = typeof result === 'object' && result.coverLetter ? result.coverLetter : result;
  const nextJobPreview = typeof result === 'object' && result.jobPreview ? result.jobPreview : formData.jobPreview;
  const nextCompanyFromJob = (typeof result === 'object' && result.inferredCompany) ? result.inferredCompany : undefined;
  const nextRoleFromJob = (typeof result === 'object' && result.inferredRole) ? result.inferredRole : undefined;

      if ((!nextJobDescription || !nextJobDescription.trim()) && nextJobPreview) {
        nextJobDescription = composeAdvert(nextJobDescription, nextJobPreview);
      }

      if (nextJobDescription && nextJobDescription !== formData.jobDescription) {
        updateStatus('Auto-filling job advert…');
      }

      // Avoid overwriting existing content with empty values
      if (!nextCoverLetter || !String(nextCoverLetter).trim()) {
        nextCoverLetter = formData.coverLetter;
      }
      if (!nextJobDescription || !String(nextJobDescription).trim()) {
        nextJobDescription = formData.jobDescription;
      }

      setFormData((prevData) => ({
        ...prevData,
        coverLetter: nextCoverLetter,
        jobDescription: nextJobDescription,
        jobPreview: nextJobPreview,
        companyName: nextCompanyFromJob ?? formData.companyName,
        targetRole: nextRoleFromJob ?? formData.targetRole,
        recipientName: formData.recipientName,
        recipientJobTitle: formData.recipientJobTitle,
        coverLetterBody: nextCoverLetter,
      }));

      if (nextCoverLetter && nextCoverLetter.length) {
        const prev = nextCoverLetter.length > 140 ? nextCoverLetter.slice(0, 137) + '…' : nextCoverLetter;
        setStatusMessage({ text: `Cover letter preview: ${prev}`, duration: 4000 });
      }

      // Ensure the section is visible to the user
      try { setActiveSection('coverLetter'); } catch {}

      if (fetchSubscription) {
        fetchSubscription();
      }

      setStatusMessage({
        text: 'Cover letter generation complete.',
        duration: 5000,
      });

      // Close the preview window if opened
      try {
        if (previewWindowRef.current && !previewWindowRef.current.closed) {
          setStatusMessage({ text: 'Closing preview window…', duration: 2000 });
          setTimeout(() => {
            try { previewWindowRef.current.close(); } catch {}
            previewWindowRef.current = null;
          }, 700);
        }
      } catch {}
    } catch (error) {
      console.error('Error generating cover letter:', error);
      setStatusMessage({
        text: 'Error generating cover letter. Please try again.',
        duration: 5000,
      });
      // Ensure we close the preview window on error as well
      try {
        if (previewWindowRef.current && !previewWindowRef.current.closed) {
          setStatusMessage({ text: 'Closing preview window…', duration: 2000 });
          setTimeout(() => {
            try { previewWindowRef.current.close(); } catch {}
            previewWindowRef.current = null;
          }, 700);
        }
      } catch {}
    } finally {
      setIsLoading(false);
    }
  };

  // Cookie save flow removed (no manual cookie pasting)

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Special handling for multiline sections
  if (['skills', 'experience', 'education', 'publications', 'summary', 'coverLetter'].includes(name)) {
      // Preserve spaces as the user types; only normalize line break characters
      const normalizedValue = value
        .replace(/\r\n/g, '\n')  // Windows CRLF -> LF
        .replace(/\r/g, '\n');    // Old Mac CR -> LF

      setFormData(prevData => ({
        ...prevData,
        [name]: normalizedValue
      }));
      return;
    }

    // If user enters a Job URL, clear other job-source fields to avoid confusion
    if (name === 'jobPostUrl') {
      const nextUrl = value;
      const trimmed = (nextUrl || '').trim();
      setFormData(prevData => ({
        ...prevData,
        jobPostUrl: nextUrl,
        // Clear conflicting inputs so the crawl becomes the single source of truth
        ...(trimmed ? {
          jobDescription: '',
          companyName: '',
          targetRole: '',
          recipientName: '',
          recipientJobTitle: '',
          jobPreview: null,
        } : {})
      }));
      // Brief user hint
      if (trimmed) {
        setStatusMessage({ text: 'Job URL detected — cleared advert and company/role fields to use the fetched posting.', duration: 5000 });
      }
      return;
    }

    // Normal handling for other sections
    if (characterLimits[name] === undefined || value.length <= characterLimits[name]) {
      setFormData(prevData => ({
        ...prevData,
        [name]: value
      }));
    }
  };

  const handleWholeResumeGenerate = async () => {
    if (!user) {
      navigate('/sign-in');
      return;
    }
    if (isGated && (credits?.remainingCredits || 0) <= 0) {
      setCreditModalType('ai_insufficient');
      setIsCreditModalOpen(true);
      return;
    }
    let messageTimer;
    try {
      clearStatusMessage();
      setIsLoading(true);
  console.log('Generating whole resume with Gemini');

      setStatusMessage({
        text: 'Starting resume generation...',
        duration: 5000,
      });

      messageTimer = setTimeout(() => {
        setStatusMessage({
          text: 'Resume generation usually takes between 5 and 30 seconds. Please be patient.',
          duration: 7000,
        });
      }, 7000);

      const updateStatus = (message) => {
        clearTimeout(messageTimer);
        setStatusMessage({ text: message, duration: 5000 });
      };

      const generatedData = await WholeResumeService.generateWholeResume(
        formData,
        characterLimits,
        updateStatus
      );
      setFormData((prevData) => ({
        ...prevData,
        ...generatedData,
      }));

      if (fetchSubscription) {
        fetchSubscription();
      }

      updateStatus('Whole resume generation complete.');
    } catch (error) {
  console.error('Error generating whole resume with Gemini:', error);
      setStatusMessage({
        text: 'Error generating whole resume. Please try again.',
        duration: 5000,
      });
    } finally {
      clearTimeout(messageTimer);
      setIsLoading(false);
      setTimeout(() => clearStatusMessage(), 3000);
    }
  };

  const handleSectionGenerate = async (section) => {
    if (!user) {
      navigate('/sign-in');
      return;
    }
    if (isGated && (credits?.remainingCredits || 0) <= 0) {
      setCreditModalType('ai_insufficient');
      setIsCreditModalOpen(true);
      return;
    }
    try {
      clearStatusMessage();
      setIsLoading(true);
  console.log(`Generating ${section} with Gemini`);
  console.debug('[DesktopApp] onGenerate start', section, { beforeLen: (formData[section]||'').toString().length });

      setStatusMessage({ text: `Generating ${section}...`, duration: 5000 });

      const updatedData = await WholeResumeService.generateSection(
        section,
        formData,
        characterLimits
      );
  console.debug('[DesktopApp] onGenerate result', section, { len: (updatedData?.[section]||'').length, preview: (updatedData?.[section]||'').slice(0,80) });
      setFormData((prevData) => ({
        ...prevData,
        [section]: updatedData[section],
      }));
  console.debug('[DesktopApp] onGenerate state updated', section, { afterLen: (updatedData?.[section]||'').length });

      if (fetchSubscription) {
        fetchSubscription();
      }

      setStatusMessage({
        text: `${section} Generation complete.`,
        duration: 5000,
      });
    } catch (error) {
  console.error(`Error generating ${section} with Gemini:`, error);
      setStatusMessage({
        text: `Error generating ${section}. ${error.message}`,
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
      setTimeout(() => clearStatusMessage(), 5000);
    }
  };

  const generatePDF = async () => {
    if (!user) {
      navigate('/sign-in');
      return;
    }

    const downloadAction = async () => {
      const blob = await pdf(<ResumePDF {...formData} />).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');

      const link = document.createElement('a');
      link.href = url;
      link.download = 'resume.pdf';
      link.click();

      setTimeout(() => URL.revokeObjectURL(url), 100);
      if (fetchSubscription) {
        fetchSubscription();
      }
    };

    if (isGated) {
      const remaining = credits?.remainingCredits || 0;
      if (remaining >= 100) {
        setCreditModalType('pdf_confirm');
        setOnCreditModalConfirm(() => async () => {
          setIsCreditModalOpen(false);
          try {
            setIsLoading(true);
            setStatusMessage({ text: 'Charging credits...', duration: 5000 });
            const token = await getToken();
            const res = await axios.post('/api/resume/charge-pdf', {}, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.data?.success) {
              if (fetchSubscription) {
                fetchSubscription();
              }
              setStatusMessage({ text: 'Generating PDF...', duration: 5000 });
              await downloadAction();
              setStatusMessage({ text: 'PDF generation complete.', duration: 5000 });
            } else {
              throw new Error('Deduction failed');
            }
          } catch (error) {
            console.error('PDF credit charge failed:', error);
            alert(error.response?.data?.error || 'Failed to deduct credits. Please try again.');
            setStatusMessage({ text: 'Deduction failed.', duration: 3000 });
          } finally {
            setIsLoading(false);
          }
        });
        setIsCreditModalOpen(true);
      } else {
        setCreditModalType('pdf_insufficient');
        setIsCreditModalOpen(true);
      }
      return;
    }

    // Normal subscriber flow
    try {
      setIsLoading(true);
      setStatusMessage({ text: 'Generating PDF...', duration: 5000 });
      await downloadAction();
      setStatusMessage({ text: 'PDF generation complete.', duration: 5000 });
    } catch (error) {
      console.error('Error generating PDF:', error);
      setStatusMessage({
        text: 'Error generating PDF. Please try again.',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearStatusMessage = useCallback(() => {
    try {
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
        statusTimeoutRef.current = null;
      }
      setStatusMessage(null);
    } catch (error) {
      console.error('Error clearing status message:', error);
    }
  }, []);

  const toggleSection = (sectionName) => {
    setActiveSection((prevSection) =>
      prevSection === sectionName ? null : sectionName
    );
  };

  // Handle ESC key and scroll lock when preview modal is open
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        setIsModalOpen(false);
      }
    };
    if (isModalOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  // Calculate dynamic scale factor for thumbnail preview container
  useEffect(() => {
    const container = zoomContainerRef.current;
    if (!container) return;

    const calculateScale = () => {
      const rect = container.getBoundingClientRect();
      const padding = 32; // padding around the document inside the card
      const targetHeight = 1122.5; // A4 height in px
      const targetWidth = 793.7; // A4 width in px
      
      const availableHeight = rect.height - padding;
      const availableWidth = rect.width - padding;
      
      const scaleH = availableHeight / targetHeight;
      const scaleW = availableWidth / targetWidth;
      
      // Fit the entire page in the viewport panel
      const nextScale = Math.max(0.1, Math.min(scaleH, scaleW));
      setResumeScale(nextScale);
    };

    calculateScale();

    const observer = new ResizeObserver(() => {
      calculateScale();
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Auto-clear status messages after their duration; avoid effect dependency on clearStatusMessage to prevent loops
  const clearStatusRef = useRef(clearStatusMessage);
  useEffect(() => {
    clearStatusRef.current = clearStatusMessage;
  }, [clearStatusMessage]);

  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => {
        clearStatusRef.current();
      }, statusMessage.duration || 3000);
      statusTimeoutRef.current = timer;
      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  const saveResume = useCallback(async () => {
    if (!user) {
      navigate('/sign-in');
      return;
    }
    try {
      const token = await getToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      await ResumeStorageService.saveResume(formData, token);
      setStatusMessage({ text: 'Resume saved successfully', duration: 3000 });
    } catch (error) {
      console.error('Error saving resume:', error);
      setStatusMessage({ 
        text: `Error saving resume: ${error.message}`, 
        duration: 3000 
      });
    } finally {
      // Intentionally do not toggle global isLoading for background auto-saves
    }
  }, [formData, getToken]);

  const loadResume = useCallback(async () => {
    if (!user) {
      navigate('/sign-in');
      return;
    }
    try {
      const token = await getToken();
      console.log('Token retrieved:', !!token);

      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const loadedData = await ResumeStorageService.loadResume(token);
      if (loadedData) {
        // Merge cautiously: do not overwrite non-empty local fields with empty server fields
        setFormData(prevData => {
          const keepIfNonEmpty = (localVal, serverVal) => {
            const s = (serverVal ?? '');
            const l = (localVal ?? '');
            return s && String(s).trim().length > 0 ? s : l;
          };
          return {
            ...prevData,
            // Prefer server for canonical resume text if present
            name: keepIfNonEmpty(prevData.name, loadedData.name),
            title: keepIfNonEmpty(prevData.title, loadedData.title),
            location: keepIfNonEmpty(prevData.location, loadedData.location),
            email: keepIfNonEmpty(prevData.email, loadedData.email),
            phone: keepIfNonEmpty(prevData.phone, loadedData.phone),
            summary: keepIfNonEmpty(prevData.summary, loadedData.summary),
            experience: keepIfNonEmpty(prevData.experience, loadedData.experience),
            skills: keepIfNonEmpty(prevData.skills, loadedData.skills),
            education: keepIfNonEmpty(prevData.education, loadedData.education),
            publications: keepIfNonEmpty(prevData.publications, loadedData.publications),
            // Crucial: never clobber a freshly generated cover letter or job fields with empty server values
            coverLetter: keepIfNonEmpty(prevData.coverLetter, loadedData.coverLetter),
            coverLetterBody: keepIfNonEmpty(prevData.coverLetterBody, loadedData.coverLetterBody),
            jobDescription: keepIfNonEmpty(prevData.jobDescription, loadedData.jobDescription),
            jobPostUrl: keepIfNonEmpty(prevData.jobPostUrl, loadedData.jobPostUrl),
            companyName: keepIfNonEmpty(prevData.companyName, loadedData.companyName),
            targetRole: keepIfNonEmpty(prevData.targetRole, loadedData.targetRole),
            applicantAddress: keepIfNonEmpty(prevData.applicantAddress, loadedData.applicantAddress),
            recipientName: keepIfNonEmpty(prevData.recipientName, loadedData.recipientName),
            recipientJobTitle: keepIfNonEmpty(prevData.recipientJobTitle, loadedData.recipientJobTitle),
            whyCompany: keepIfNonEmpty(prevData.whyCompany, loadedData.whyCompany),
            keyAchievements: keepIfNonEmpty(prevData.keyAchievements, loadedData.keyAchievements),
            keyRoleSkills: keepIfNonEmpty(prevData.keyRoleSkills, loadedData.keyRoleSkills),
            jobPreview: loadedData.jobPreview ?? prevData.jobPreview,
          };
        });
        setStatusMessage({ text: 'Resume loaded successfully', duration: 3000 });
      }
    } catch (error) {
      console.error('Error loading resume:', error);
      setStatusMessage({ 
        text: `Error loading resume: ${error.message}`, 
        duration: 3000 
      });
    } finally {
  // Do not toggle global isLoading here to avoid visual jitter on mount
    }
  }, [getToken]);

  // Update initialization effect
  useEffect(() => {
    let mounted = true;
    const initializeResume = async () => {
  if (mounted && user && !hasInitialLoadRef.current) {
        try {
          let retries = 3;
          while (retries > 0 && mounted) {
            try {
              await loadResume();
              hasInitialLoadRef.current = true;
              break;
            } catch (error) {
              retries--;
              if (retries === 0) throw error;
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        } catch (error) {
          if (!mounted) return;
          console.error('Failed to initialize resume:', error);
          setStatusMessage({
            text: 'Failed to load resume. Please refresh the page.',
            duration: 5000
          });
        }
      }
    };
    initializeResume();
    return () => { mounted = false; };
  }, [user, loadResume, setStatusMessage]);

  // Update auto-save effect
  useEffect(() => {
    const debouncedSave = debounce(async () => {
      if (user) {
        try {
          await saveResume();
        } catch (error) {
          console.error('Failed to auto-save:', error);
        }
      }
    }, 5000);

    if (user) {
      debouncedSave();
    }
    return () => debouncedSave.cancel();
  }, [formData, user, saveResume]);

  useEffect(() => {
    if (user) {
      const pendingStr = sessionStorage.getItem('coach_pending');
      if (pendingStr) {
        try {
          const pending = JSON.parse(pendingStr);
          const age = Date.now() - pending.timestamp;
          if (age < 600000 && pending.returnTab === 'coach') {
            setIsChatbotOpen(true);
          }
        } catch (e) {
          console.error("Error checking pending coach redirect:", e);
        }
      }
    }
  }, [user]);

  // Debounce function
  const debounce = (func, delay) => {
    let timeoutId;
    const debouncedFunc = (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
    debouncedFunc.cancel = () => clearTimeout(timeoutId);
    return debouncedFunc;
  };

  // const handlePremiumFeature = () => {
  //   if (subscriptionTier === 'free') {
  //     setStatusMessage({ text: 'Upgrade to access this feature', duration: 3000 });
  //     return;
  //   }
  // };

  // Add this new function to handle upgrade button click
  const handleUpgradeClick = () => {
    closeSettings();
    navigate('/settings/billing');
  };

  const toggleMenu = () => {
  const next = !isMenuOpen;
  setIsMenuOpen(next);
  const el = document.querySelector('.slide-nav');
  if (el) el.classList.toggle('open', next);
  };

  const closeMenu = useCallback(() => {
  if (isMenuOpen) setIsMenuOpen(false);
  const el = document.querySelector('.slide-nav');
  if (el) el.classList.remove('open');
  }, [isMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMenuOpen &&
        menuToggleRef.current &&
        !menuToggleRef.current.contains(event.target) &&
        slideNavRef.current &&
        !slideNavRef.current.contains(event.target)
      ) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, closeMenu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Ignore clicks on the help button so it doesn't re-toggle via both handlers
      const helpButtonContainer = document.querySelector('.help-button-container');
      const clickedHelpButton = helpButtonContainer && helpButtonContainer.contains(event.target);

      if (clickedHelpButton) return;

      if (chatbotRef.current && !chatbotRef.current.contains(event.target) && isChatbotOpen) {
        toggleChatbot();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isChatbotOpen, toggleChatbot]);

  // Update the settings menu JSX to include the new upgrade button
  const settingsMenuJSX = (
    <>
      <div className="setting-item" style={{ '--item-index': 1 }}>
        <label htmlFor="darkModeToggle">Dark Mode</label>
        <label className="switch">
          <input
            id="darkModeToggle"
            type="checkbox"
            checked={darkMode}
            onChange={handleDarkModeToggle}
          />
          <span className="slider"></span>
        </label>
      </div>
      <div className="setting-item" style={{ '--item-index': 2 }}>
        <label htmlFor="templateSelect">Resume Template</label>
        <select
          id="templateSelect"
          onChange={(e) => changeTemplate(e.target.value)}
        >
          <option value="standard">Standard</option>
          <option value="modern">Modern</option>
          <option value="creative">Creative</option>
        </select>
      </div>
      {user ? (
        <div className="setting-item" style={{ '--item-index': 3 }}>
          <button className="logout-button" onClick={() => { signOut(); closeSettings(); }}>Log Out</button>
        </div>
      ) : (
        <div className="setting-item" style={{ '--item-index': 3 }}>
          <button className="logout-button login-button" onClick={() => { navigate('/sign-in'); closeSettings(); }}>Sign In / Register</button>
        </div>
      )}
      {user && (
        <div className="setting-item" style={{ '--item-index': 4 }}>
          <label>Current Plan: {tier}</label>
          <button onClick={handleUpgradeClick}>Manage Subscription</button>
        </div>
      )}
      {user?.email === 'andrew.green.contact@gmail.com' && (
        <div className="setting-item" style={{ '--item-index': 5 }}>
          <button 
            onClick={() => { closeSettings(); navigate('/admin'); }}
            style={{ 
              background: 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)', 
              color: '#000', 
              fontWeight: 'bold',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              cursor: 'pointer',
              width: '100%',
              marginTop: '8px'
            }}
          >
            Go to Admin Control Board
          </button>
        </div>
      )}
      <div className="setting-item" style={{ '--item-index': 6, display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '15px', borderTop: '1px solid var(--border-color)', paddingTop: '15px' }}>
        <button 
          onClick={() => { closeSettings(); navigate('/privacy'); }} 
          style={{ background: 'none', border: 'none', color: 'var(--text-color)', opacity: 0.8, cursor: 'pointer', fontSize: '12px', textDecoration: 'underline', padding: 0 }}
        >
          Privacy Policy
        </button>
        <span style={{ color: 'var(--text-color)', opacity: 0.5, fontSize: '12px' }}>|</span>
        <button 
          onClick={() => { closeSettings(); navigate('/terms'); }} 
          style={{ background: 'none', border: 'none', color: 'var(--text-color)', opacity: 0.8, cursor: 'pointer', fontSize: '12px', textDecoration: 'underline', padding: 0 }}
        >
          Terms of Service
        </button>
      </div>
    </>
  );

  // Removed unused getTokenWithRetry helper

  // Allow unauthenticated guest users to browse the dashboard editor

  return (
    <div className={`desktop-app ${darkMode ? 'dark-mode' : ''} ${isAnimating ? 'animating' : ''}`}>
      <h1 style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>Career Vision AI — Professional AI Resume Builder & Interview Simulator</h1>
      <header className="app-header">
        <div className="header-content">
          <div className="logo-banner">
            <Link to="/">
              <img src="/logo.png" alt="Career Vision AI Logo" width="333" height="83" style={{ cursor: 'pointer', aspectRatio: '333/83', objectFit: 'contain' }} fetchpriority="high" />
            </Link>
          </div>
          {statusMessage && (
            <Popup
              message={statusMessage.text}
              onClose={clearStatusMessage}
              duration={statusMessage.duration || 3000}
              type="header-popup"
              onVisibilityChange={(v) => {
                // Avoid redundant state updates that can cause re-render loops
                if (isPopupVisibleRef.current !== v) {
                  setIsPopupVisible(v);
                }
              }}
            />
          )}
        </div>
        <div className="header-right">
          {credits && (
            <div className={`credit-display ${isPopupVisible ? 'invisible-content' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '16px' }}>
              <span className="credit-text" style={{ fontSize: '0.9rem', fontWeight: '500', color: 'var(--text-color, #333)', opacity: 0.85 }}>
                Credits: <strong style={{ color: '#ff9800' }}>{credits.remainingCredits}</strong>
              </span>
              <button 
                onClick={() => navigate('/pricing', { state: { activeTab: 'credits' } })} 
                style={{ 
                  background: 'linear-gradient(135deg, #ff9800 0%, #e65100 100%)', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: '4px', 
                  padding: '4px 10px', 
                  fontSize: '0.8rem', 
                  fontWeight: '600', 
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(230,81,0,0.2)'
                }}
              >
                Top Up
              </button>
            </div>
          )}
          <span className={`welcome-message ${isPopupVisible ? 'invisible-content' : ''}`}>
            Welcome, {user?.displayName?.trim().split(/\s+/)[0] || user?.email?.split('@')[0]?.split('.')[0] || formData?.name?.trim().split(/\s+/)[0] || 'Guest'}!
          </span>
          <button
            className={`generate-button ${isPopupVisible ? 'invisible-content' : ''}`}
            onClick={handleWholeResumeGenerate}
            disabled={isLoading}
          >
            {isLoading ? <div className="spinner"></div> : 'Generate Resume'}
          </button>
          <button
            className={`generate-button ${isPopupVisible ? 'invisible-content' : ''}`}
            onClick={generatePDF}
            disabled={isLoading}
          >
            {isLoading ? <div className="spinner"></div> : 'Generate PDF'}
          </button>
          <button
            className={`settings-button settings-icon ${
              isSpinning ? 'spinning' : ''
            }`}
            onClick={handleSettingsClick}
            ref={settingsButtonRef}
            aria-label="Settings"
            disabled={isClosing || isSpinning}
          >
            <FaCog />
          </button>
          <div className="menu-toggle-wrapper">
            <button 
              ref={menuToggleRef}
              className={`menu-toggle ${isMenuOpen ? 'open' : ''}`}
              onClick={toggleMenu} 
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
            >
              <div className="menu-icon-wrapper">
                <span className="menu-icon"></span>
              </div>
            </button>
          </div>
        </div>
      </header>

      <nav ref={slideNavRef} className={`slide-nav ${isMenuOpen ? 'open' : ''}`}>
        <Link to="/" onClick={closeMenu}>Home</Link>
        <Link to="/features" onClick={closeMenu}>Features</Link>
        <Link to="/pricing" onClick={closeMenu}>Pricing</Link>
        <Link to="/about" onClick={closeMenu}>About</Link>
        <Link to="/contact" onClick={closeMenu}>Contact</Link>
        <Link to="/blog" onClick={closeMenu}>Blog</Link>
        <Link to="/interview-simulator" onClick={closeMenu}>Interview Simulator</Link>
        {user?.email === 'andrew.green.contact@gmail.com' && (
          <Link to="/admin" onClick={closeMenu} style={{ color: '#fbbf24', fontWeight: 'bold' }}>Admin Panel</Link>
        )}
      </nav>

      <div 
        className={`overlay ${isMenuOpen ? 'open' : ''}`} 
        onClick={closeMenu}
      ></div>

      {isSettingsOpen && (
        <>
          <FocusTrap>
            <div
              ref={settingsPopupRef}
              className={`settings-popup ${isClosing ? 'hide' : ''}`}
              aria-modal="true"
              role="dialog"
            >
              <h3>Settings</h3>
              {settingsMenuJSX}
            </div>
          </FocusTrap>
          {/* Backdrop */}
          <div
            className={`settings-backdrop ${
              isClosing ? 'fade-out' : 'fade-in'
            }`}
            onClick={closeSettings}
          ></div>
        </>
      )}

      <div className="resume-builder-container">
        <div className={`preview-container ${isGated ? 'gated-preview' : ''}`} onCopy={handleCopyAttempt}>
          <div
            ref={zoomContainerRef}
            className="resume-zoom-container"
            onClick={() => setIsModalOpen(true)}
            style={{ '--resume-scale': resumeScale }}
          >
            <div className="resume-preview-overlay">
              <div className="resume-preview-overlay-badge">
                <FaEye /> Click to Expand
              </div>
            </div>
            <div ref={resumeRef} className="resume-content">
              <Resume {...formData} />
            </div>
          </div>
    </div>
  <div className={`edit-panel-container`}>
          <EditPanel
            {...formData}
            onInputChange={handleInputChange}
            onGenerateClick={handleSectionGenerate}
            isLoading={isLoading}
            characterLimits={characterLimits}
            activeSection={activeSection}
            toggleSection={toggleSection}
            user={user}
            tier={tier}
            credits={credits}
            onShowCreditGate={handleShowCreditGate}
            handleCoverLetterGenerate={handleCoverLetterGenerate}
            setActiveSection={setActiveSection}
            fetchSubscription={fetchSubscription}
          />
        </div>
      </div>
      <ChatbotPopup
        ref={chatbotRef}
        isOpen={isChatbotOpen}
        onClose={toggleChatbot}
        formData={formData}
        onDataUpdate={(updatedData) =>
          setFormData((prevData) => ({ ...prevData, ...updatedData }))
        }
        onProposeUpdate={({ sectionKey, sectionName, proposedContent }) => {
          setProposedUpdate({
            sectionKey,
            sectionName,
            currentContent: formData[sectionKey] || '',
            proposedContent
          });
        }}
        triggerErrorPopup={(msg) =>
          setStatusMessage({ text: msg, duration: 3000, type: 'bottom' })
        }
        darkMode={darkMode}
        onMessageSent={fetchSubscription}
        user={user}
        navigate={navigate}
        credits={credits}
        isGated={isGated}
        onShowCreditGate={handleShowCreditGate}
      />
      {typeof document !== 'undefined'
        ? ReactDOM.createPortal(
            (
              <div className="help-button-container">
                <button
                  className="help-me-button"
                  onClick={toggleChatbot}
                  aria-label="Open Chat Assistant"
                >
                  <img src="/helpButton.png" alt="Ask A.i" />
                </button>
              </div>
            ),
            document.body
          )
        : (
            <div className="help-button-container">
              <button
                className="help-me-button"
                onClick={toggleChatbot}
                aria-label="Open Chat Assistant"
              >
                <img src="/helpButton.png" alt="Ask A.i" />
              </button>
            </div>
          )}

  {/* Manual cookie modal removed for automated flow */}
      <CreditGateModal
        isOpen={isCreditModalOpen}
        onClose={() => setIsCreditModalOpen(false)}
        onConfirm={onCreditModalConfirm}
        type={creditModalType}
        balance={credits?.remainingCredits || 0}
      />

      {/* Fullscreen Document Preview Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="resume-modal-backdrop"
              onClick={() => setIsModalOpen(false)}
            />
            <div 
              className="resume-modal-scroll-wrapper"
              onClick={() => setIsModalOpen(false)}
            >
              <button 
                className="resume-modal-close-btn"
                onClick={() => setIsModalOpen(false)}
                aria-label="Close Preview"
              >
                <FaTimes />
              </button>
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 350 }}
                className="resume-modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <Resume {...formData} />
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {proposedUpdate && (
        <ResumeUpdateModal
          sectionName={proposedUpdate.sectionName}
          currentContent={proposedUpdate.currentContent}
          proposedContent={proposedUpdate.proposedContent}
          onApprove={() => {
            setFormData((prevData) => ({
              ...prevData,
              [proposedUpdate.sectionKey]: proposedUpdate.proposedContent,
            }));
            setProposedUpdate(null);
            setStatusMessage({ text: `${proposedUpdate.sectionName} updated successfully!`, duration: 3000, type: 'bottom' });
          }}
          onReject={() => {
            setProposedUpdate(null);
          }}
        />
      )}
    </div>
  );
};

export default DesktopApp;
